import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  ClipboardList, Send, CheckCircle, AlertTriangle, Clock,
  FileEdit, Save, RefreshCw, ChevronRight, Info
} from 'lucide-react';
import { toast } from 'sonner';

export default function PortalAtualizacaoPage() {
  const { campanhaId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [campanha, setCampanha] = useState(null);
  const [instituicao, setInstituicao] = useState(null);
  const [formData, setFormData] = useState({});
  const [diferencas, setDiferencas] = useState([]);

  useEffect(() => {
    loadData();
  }, [campanhaId]);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('portal_token');
      const userStr = localStorage.getItem('portal_user');
      const user = userStr ? JSON.parse(userStr) : {};
      const instituicaoId = user.instituicao_id;

      // Load campanha details
      const [campanhaRes, instRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/portal/campanhas/${campanhaId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/instituicoes/${instituicaoId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (campanhaRes.ok) {
        const campanhaData = await campanhaRes.json();
        setCampanha(campanhaData);
        
        // Initialize form with current data
        if (campanhaData.campos_solicitados) {
          const initialData = {};
          campanhaData.campos_solicitados.forEach(campo => {
            initialData[campo] = campanhaData.dados_atuais?.[campo] || '';
          });
          setFormData(initialData);
        }
      }

      if (instRes.ok) {
        const instData = await instRes.json();
        setInstituicao(instData);
        
        // Pre-fill with institution data if no campanha data
        if (!campanha?.dados_atuais) {
          setFormData(prev => ({
            ...prev,
            nome: instData.nome || '',
            morada: instData.morada || '',
            codigo_postal: instData.codigo_postal || '',
            telefone: instData.telefone || '',
            email_principal: instData.email_principal || '',
            website: instData.website || '',
            nif: instData.nif || '',
            pessoa_contacto: instData.pessoa_contacto || ''
          }));
        }
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Erro ao carregar dados da campanha');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value };
      
      // Calculate differences
      const difs = [];
      Object.keys(newData).forEach(key => {
        const valorAtual = instituicao?.[key] || '';
        const valorNovo = newData[key] || '';
        if (valorAtual !== valorNovo && valorNovo) {
          difs.push({
            campo: key,
            valor_atual: valorAtual,
            valor_novo: valorNovo
          });
        }
      });
      setDiferencas(difs);
      
      return newData;
    });
  };

  const handleSubmit = async () => {
    if (diferencas.length === 0) {
      toast.info('Nenhuma alteração detectada');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('portal_token');
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/portal/campanhas/${campanhaId}/responder`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            dados_novos: formData,
            diferencas: diferencas
          })
        }
      );

      if (response.ok) {
        toast.success('Dados submetidos com sucesso! Aguarde validação do administrador.');
        navigate('/portal/dashboard');
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Erro ao submeter dados');
      }
    } catch (error) {
      toast.error('Erro ao submeter dados');
    } finally {
      setSubmitting(false);
    }
  };

  const CAMPOS_CONFIG = {
    nome: { label: 'Nome da Instituição', type: 'text', required: true },
    morada: { label: 'Morada', type: 'text' },
    codigo_postal: { label: 'Código Postal', type: 'text' },
    localidade: { label: 'Localidade', type: 'text' },
    telefone: { label: 'Telefone', type: 'tel' },
    email_principal: { label: 'Email Principal', type: 'email' },
    website: { label: 'Website', type: 'url' },
    nif: { label: 'NIF', type: 'text' },
    niss: { label: 'NISS (Segurança Social)', type: 'text' },
    pessoa_contacto: { label: 'Pessoa de Contacto', type: 'text' },
    representante_legal: { label: 'Representante Legal', type: 'text' },
    total_utentes: { label: 'Total de Utentes', type: 'number' },
    num_funcionarios: { label: 'Número de Funcionários', type: 'number' },
    observacoes: { label: 'Observações', type: 'textarea' }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="atualizacao-loading">
        <div className="w-8 h-8 border-2 border-slate-200 border-t-[#0066CC] rounded-full animate-spin"></div>
      </div>
    );
  }

  const camposSolicitados = campanha?.campos_solicitados || Object.keys(CAMPOS_CONFIG).slice(0, 8);

  return (
    <div className="space-y-6 max-w-4xl mx-auto" data-testid="portal-atualizacao-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
          <ClipboardList className="w-6 h-6 text-[#0066CC]" />
          Campanha de Atualização de Dados
        </h1>
        <p className="text-slate-500">
          {campanha?.nome || 'Atualize os dados da sua instituição'}
        </p>
      </div>

      {/* Info Card */}
      <Card className="bg-blue-50 dark:bg-blue-900/20 border-blue-200">
        <CardContent className="pt-4">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-blue-800 dark:text-blue-300">
                Os campos abaixo estão pré-preenchidos com os dados atuais da sua instituição.
                Atualize apenas os campos que necessitam de alteração. Após submeter, 
                as alterações serão revistas pela ENTRAJUDA antes de serem aplicadas.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Form */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileEdit className="w-5 h-5" />
            Dados da Instituição
          </CardTitle>
          <CardDescription>
            Preencha ou atualize os campos solicitados
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-6">
            {camposSolicitados.map((campo) => {
              const config = CAMPOS_CONFIG[campo] || { label: campo, type: 'text' };
              const valorAtual = instituicao?.[campo] || '';
              const valorForm = formData[campo] || '';
              const temDiferenca = valorAtual !== valorForm && valorForm;

              return (
                <div key={campo} className="space-y-1.5">
                  <label className="text-sm font-medium text-slate-700 dark:text-slate-300 flex items-center gap-2">
                    {config.label}
                    {config.required && <span className="text-red-500">*</span>}
                    {temDiferenca && (
                      <Badge variant="outline" className="text-amber-600 border-amber-300 text-xs">
                        Alterado
                      </Badge>
                    )}
                  </label>
                  
                  {config.type === 'textarea' ? (
                    <Textarea
                      value={valorForm}
                      onChange={(e) => handleChange(campo, e.target.value)}
                      placeholder={`${config.label}...`}
                      rows={3}
                    />
                  ) : (
                    <Input
                      type={config.type}
                      value={valorForm}
                      onChange={(e) => handleChange(campo, e.target.value)}
                      placeholder={`${config.label}...`}
                      className={temDiferenca ? 'border-amber-400 bg-amber-50 dark:bg-amber-900/20' : ''}
                    />
                  )}
                  
                  {valorAtual && valorAtual !== valorForm && (
                    <p className="text-xs text-slate-500">
                      Valor atual: <span className="font-medium">{valorAtual}</span>
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Differences Summary */}
      {diferencas.length > 0 && (
        <Card className="border-amber-200 bg-amber-50 dark:bg-amber-900/20">
          <CardHeader>
            <CardTitle className="text-amber-800 dark:text-amber-300 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Alterações Detectadas ({diferencas.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {diferencas.map((dif, idx) => (
                <div key={idx} className="flex items-center gap-2 text-sm">
                  <ChevronRight className="w-4 h-4 text-amber-600" />
                  <span className="font-medium">{CAMPOS_CONFIG[dif.campo]?.label || dif.campo}:</span>
                  <span className="text-red-500 line-through">{dif.valor_atual || '(vazio)'}</span>
                  <span className="text-slate-400">→</span>
                  <span className="text-green-600">{dif.valor_novo}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Actions */}
      <div className="flex justify-end gap-4">
        <Button variant="outline" onClick={() => navigate('/portal/dashboard')}>
          Cancelar
        </Button>
        <Button 
          onClick={handleSubmit}
          disabled={submitting || diferencas.length === 0}
          className="bg-[#0066CC] hover:bg-[#0055AA]"
        >
          {submitting ? (
            <>
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              A submeter...
            </>
          ) : (
            <>
              <Send className="w-4 h-4 mr-2" />
              Submeter Alterações
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
